<template>
    <div class="q-pt-sm q-px-sm">
        <div class="text-subtitle2 q-mb-xs">
            {{ label }}
            <help-icon v-if="helpText" :text="helpText" class="text-grey q-ml-xs"></help-icon>
        </div>

        <q-slider
            v-model="internalValue"
            :min="min"
            :max="max"
            :step="step"
            label
            label-always
            color="primary"
            class="q-mt-sm"
            v-bind="$attrs"
        ></q-slider>

        <div class="q-mt-md" v-if="preview">
            <div class="text-caption text-grey q-mb-xs">{{ previewLabel }}</div>
            <div class="weekview-preview-container q-pa-sm rounded-borders" style="overflow-x: auto; max-width: 100%; border: 1px solid rgba(0, 0, 0, 0.12); background-color: rgba(0, 0, 0, 0.02);">
                <div class="weekview-wrapper" style="overflow-x: visible; cursor: default;">
                    <div class="row weekview weekviewHeader" style="display: inline-flex; width: max-content; min-width: 0;">
                        <!-- Timeline Column on the left -->
                        <div class="col timeline-left" style="flex: 0 0 90px !important; width: 90px; min-width: 90px; max-width: 90px; background: transparent;">
                            <div class="daycolumn">
                                <div class="dayviewDate text-center">
                                    <q-icon class="text-grey-8" name="calendar_today" size="2em"></q-icon>
                                </div>
                                <div class="dayTimeline day">
                                    <div class="item">
                                        <div class="itemTitle">&nbsp;</div>
                                        <div class="itemTime shiftLength30">08:00 - 08:30</div>
                                        <div class="itemTime shiftLength30">08:30 - 09:00</div>
                                        <div class="itemTime shiftLength30">09:00 - 09:30</div>
                                        <div class="itemTime shiftLength30">09:30 - 10:00</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Day Column containing ALL active items and campaigns -->
                        <div class="col" style="flex: 0 0 auto !important; width: auto;">
                            <div class="daycolumn" :style="{ width: totalDayWidth + 'px', minWidth: totalDayWidth + 'px' }">
                                <div class="dayviewDate text-center">
                                    <q-btn size="sm" no-caps color="primary" class="ellipsis" style="margin: 0; padding: 0.3rem 0.5rem; width: 100%;">
                                        {{ sampleDate }}
                                    </q-btn>
                                </div>
                                <div class="day" style="display: flex; flex-wrap: nowrap;">
                                    <div
                                        v-for="el in computedElements"
                                        :key="el.key"
                                        class="item"
                                        :class="{ 'item--current': el.is_current }"
                                        :style="{
                                            width: (el.is_current ? internalValue : (el.custom_width || 75)) + 'px',
                                            minWidth: (el.is_current ? internalValue : (el.custom_width || 75)) + 'px',
                                            maxWidth: (el.is_current ? internalValue : (el.custom_width || 75)) + 'px',
                                            '--item-min-width': (el.is_current ? internalValue : (el.custom_width || 75)) + 'px',
                                            borderLeft: el.is_current ? '2px solid #1976d2' : '1px solid transparent',
                                            borderRight: el.is_current ? '2px solid #1976d2' : '1px solid transparent',
                                        }"
                                    >
                                        <div
                                            class="itemTitle"
                                            :style="el.is_current ? { backgroundColor: '#1976d2', color: '#ffffff', borderRadius: '3px', fontWeight: 'bold' } : {}"
                                        >
                                            <span class="ellipsis" style="display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding: 0 2px;">
                                                {{ el.is_current ? displayedTitle : (el.short_name || el.name) }}
                                            </span>
                                        </div>
                                        <div
                                            class="itemTime free shiftLength30 text-center"
                                            :style="el.is_current ? { backgroundColor: '#bbdefb !important' } : {}"
                                        >
                                            <div><a>&nbsp;</a></div>
                                        </div>
                                        <div
                                            class="itemTime free shiftLength30 text-center"
                                            :style="el.is_current ? { backgroundColor: '#bbdefb !important' } : {}"
                                        >
                                            <div><a>&nbsp;</a></div>
                                        </div>
                                        <div
                                            class="itemTime closed shiftLength30 text-center"
                                            :style="el.is_current ? { backgroundColor: '#e3f2fd !important' } : {}"
                                        >
                                            &nbsp;
                                        </div>
                                        <div
                                            class="itemTime free shiftLength30 text-center"
                                            :style="el.is_current ? { backgroundColor: '#bbdefb !important' } : {}"
                                        >
                                            <div><a>&nbsp;</a></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <input type="hidden" :name="name" :value="internalValue">
    </div>
</template>

<script>
import { ref, watch, computed, onMounted, onUnmounted } from "vue";
import HelpIcon from "./HelpIcon.vue";
import dayjs from "dayjs";

export default {
    name: "SliderWrapper",
    components: {
        HelpIcon,
    },
    props: {
        modelValue: {
            type: Number,
            default: 75,
        },
        name: String,
        label: String,
        min: {
            type: Number,
            default: 20,
        },
        max: {
            type: Number,
            default: 500,
        },
        step: {
            type: Number,
            default: 1,
        },
        helpText: String,
        preview: {
            type: Boolean,
            default: false,
        },
        previewElements: {
            type: Array,
            default: () => [],
        },
    },
    setup(props) {
        const internalValue = ref(Number(props.modelValue) || 75);
        const displayedTitle = ref("Item Title");

        watch(
            () => props.modelValue,
            (val) => {
                internalValue.value = Number(val) || 75;
            },
        );

        const updateTitle = () => {
            const titleInput = document.querySelector(
                'input[name="itemName"], input[name="campaignTitleShort"], input[name="itemTypeName"], input[name="campaignTitleLong"]'
            );
            if (titleInput && titleInput.value && titleInput.value.trim().length > 0) {
                displayedTitle.value = titleInput.value.trim();
            } else {
                // Find current element in props.previewElements if available
                const currentEl = props.previewElements?.find((e) => e.is_current);
                if (currentEl && (currentEl.short_name || currentEl.name)) {
                    displayedTitle.value = currentEl.short_name || currentEl.name;
                } else {
                    const isGerman = (window.locale || "").startsWith("de");
                    displayedTitle.value = isGerman ? "Element" : "Item Title";
                }
            }
        };

        const computedElements = computed(() => {
            if (props.previewElements && props.previewElements.length > 0) {
                return props.previewElements.map((el, index) => ({
                    ...el,
                    key: el.id ? `${el.type}_${el.id}` : `new_${index}`,
                }));
            }
            return [
                {
                    key: "current_single",
                    id: null,
                    type: "item",
                    short_name: displayedTitle.value,
                    name: displayedTitle.value,
                    custom_width: internalValue.value,
                    is_current: true,
                },
            ];
        });

        const totalDayWidth = computed(() => {
            return computedElements.value.reduce((sum, el) => {
                const width = el.is_current ? internalValue.value : (Number(el.custom_width) || 75);
                return sum + width;
            }, 0);
        });

        const sampleDate = computed(() => {
            try {
                const d = dayjs();
                const formatted = d.format("dd, DD.MM.");
                return formatted.charAt(0).toUpperCase() + formatted.slice(1);
            } catch {
                return "Mo, 17.08.";
            }
        });

        const previewLabel = computed(() => {
            const isGerman = (window.locale || "").startsWith("de");
            return isGerman ? "Vorschau" : "Preview";
        });

        onMounted(() => {
            updateTitle();
            document.addEventListener("input", updateTitle);
        });

        onUnmounted(() => {
            document.removeEventListener("input", updateTitle);
        });

        return {
            internalValue,
            displayedTitle,
            computedElements,
            totalDayWidth,
            sampleDate,
            previewLabel,
        };
    },
};
</script>
